import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_EXERCISE_RESIGN, JOIN_TO_EXERCISE } from "../../../../../../api/apiRoutes";
import { Exercise } from "../../../../../../models/Exercise";
import { Ticket } from "../../../../../../models/Ticket";
import { SwimmingLesson } from "../../../../../../models/SwimmingScheduleData";
import { State } from "../../../../../../store";
import { setSuccessMessage } from "../../../../../../store/alerts/alertsActions";
import { errorMessageFromApi, UNKNOWN_ERROR } from "../../../../../../utils/apiErrorMessages";
import useApiRequest from "../../../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../../../utils/hooks/useFacility";
import getId from "../../../../../../../utils/getId";

export type ChangeSwimmingTermStage = "resign" | "join";

interface ChangeSwimmingTermResult {
  ok: boolean;
  stage?: ChangeSwimmingTermStage;
  error: string;
}

export default function useChangeSwimmingTerm() {
  const facilityIds = useFacility();
  const { post } = useApiRequest();
  const dispatch = useDispatch();
  const userDataId = useSelector((state: State) => state.auth.userData.objectId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resignedForRef = useRef("");

  const changeTerm = async (
    fromExercise: Exercise,
    toLesson: SwimmingLesson,
    ticket: Ticket,
    successMessage: string,
  ): Promise<ChangeSwimmingTermResult> => {
    if (isSubmitting) {
      return { ok: false, error: "" };
    }

    const passIds = getId(ticket);
    const exerciseIds = getId(fromExercise);
    if (!passIds || !exerciseIds) {
      return { ok: false, error: UNKNOWN_ERROR };
    }

    setIsSubmitting(true);
    try {
      if (resignedForRef.current !== exerciseIds) {
        const resignResponse = await post(GET_EXERCISE_RESIGN(exerciseIds, facilityIds));
        if (resignResponse.isError) {
          return { ok: false, stage: "resign", error: errorMessageFromApi(resignResponse) || UNKNOWN_ERROR };
        }
        resignedForRef.current = exerciseIds;
      }

      const joinResponse = await post(JOIN_TO_EXERCISE(facilityIds, passIds), {
        exerciseIds: toLesson.objectId,
        webUserIds: `0,${userDataId}`,
        assigned: false,
      });
      if (joinResponse.isError) {
        return { ok: false, stage: "join", error: errorMessageFromApi(joinResponse) || UNKNOWN_ERROR };
      }

      resignedForRef.current = "";
      dispatch(setSuccessMessage(successMessage));
      return { ok: true, error: "" };
    } catch {
      return { ok: false, error: UNKNOWN_ERROR };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { changeTerm, isSubmitting };
}

